import React, { useCallback, useState } from "react";
import { FaBookmark, FaClock, FaRegBookmark } from "react-icons/fa";
import tradingView from "@/assets/icons/tradingview.svg";
import locked_icon from "@/assets/icons/icon_locked.png";
import chainIcon from "@/assets/icons/chainIcon.svg";
import { motion } from "framer-motion";
import mini_trade_card_bg from "@/assets/mini_trade_card_bg.gif";
import Image from "next/image";
import {
    inRangeOrNot,
    isFutures,
    isFuturesOrOptions,
    isMcxFuturesOrOptions,
    isOptions,
    termColor,
    termText,
    toStockDetailFromTradeCard,
    unLockTradeCard
} from "@/lib/tradeCards";
import { useSubscription } from "react-stomp-hooks";
import { BsInfoCircleFill } from "react-icons/bs";
import TradeHistoryPopUp from "./TradeHistoryPopUp";
import WatchListButton from "../WatchListButton";
import { stopLossPopUp } from "./StopLossPopUp";
import { Mixpanel } from "@/elements/Mixpanel";
import { TradeCardBuyBtn } from "@/components/PlaceOrder/BuySellBtn";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import Hint from "@/components/Hint";
import { capitalized, cn, putComma } from "@/lib/utils";
import moment from "moment";
import { setBuyProPopUp, setKycNewPopUp } from "@/store/features/userDetailsSlice";

export function LiveTradeIdeaCardV2({ ele, getTradeCard, term, openAboutTrade, tradeId }) {
    const router = useRouter();
    const [stockPrices, setStockPrices] = useState({});
    const dispatch = useDispatch();

    const isDisabled = ["MCX-FUT", "MCX-OPT", "COMMODITY"].includes(ele?.term);

    useSubscription(`/stock/live-price/${ele?.finCode}`, (res) => {
        const newPrice = JSON.parse(res.body).ltpPrice;
        setStockPrices((prevPrices) => ({
            ...prevPrices,
            [ele?.finCode]: newPrice
        }));
    });

    const calcPotential = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            if (price && ele?.targetPrice) {
                let final = 0.0;
                if (isFutures(ele.term)) {
                    final = (ele?.targetPrice - price) * ele?.lotSize;
                    if (ele.recommendationType === "SELL") {
                        final *= -1;
                    }
                } else if (isOptions(ele?.term)) {
                    let netGain = calcNetGainLoss(ele);
                    if (netGain) {
                        final = ele?.targetPrice - netGain;
                    }
                } else {
                    final = ((ele?.targetPrice - price) / price) * 100;
                }
                return parseFloat(final.toFixed(2));
            }
            return 0.0;
        },
        [stockPrices]
    );

    const calcNetGainLoss = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            if (price && ele?.suggestedPrice) {
                let final = 0.0;
                if (isFutures(ele.term)) {
                    final = (price - ele?.suggestedPrice) * ele?.lotSize;
                    if (ele.recommendationType === "SELL") {
                        final *= -1;
                    }
                    final += ele?.adjustedGain ?? 0;
                } else if (isOptions(ele?.term)) {
                    let total = 0.0;
                    if (ele.tradeCardOptionsDataList) {
                        for (let tradeOption of ele.tradeCardOptionsDataList) {
                            const optionPrice = stockPrices?.[tradeOption?.finCode] ?? tradeOption?.nseLtpPrice;
                            if (optionPrice) {
                                total +=
                                    tradeOption.ratio *
                                    (optionPrice - tradeOption.suggestedPrice) *
                                    tradeOption.lotSize *
                                    (tradeOption.recommendationType === "BUY" ? -1 : 1);
                            }
                        }
                    }
                    final = total * -1;
                } else {
                    const unrealizedGain = ele?.quantityAvailable * (price - ele?.suggestedPrice);
                    final = ((ele?.realizedGain + unrealizedGain) * 100) / (ele?.totalInvestment ?? 1);
                }
                return parseFloat(final.toFixed(2));
            }
            return 0.0;
        },
        [stockPrices]
    );

    const calcNetPriceChange = useCallback(
        (ele) => {
            const price = stockPrices?.[ele?.finCode] ?? ele?.ltpPrice;
            const closePrice = ele?.closePrice;
            if (price && closePrice) {
                const change = price - closePrice;
                const percentageChange = (change / closePrice) * 100;
                return {
                    change: parseFloat(change.toFixed(2)),
                    percentageChange: parseFloat(percentageChange.toFixed(2))
                };
            }
            return { change: 0.0, percentageChange: 0.0 };
        },
        [stockPrices]
    );

    const handleButtonAction = (ele) => {
        if (ele?.tradeCardLockedStateActionDto?.action == "KYC") {
            dispatch(setKycNewPopUp(true));
        } else if (ele?.tradeCardLockedStateActionDto?.action === "UNLOCK_TRADE_CARD") {
            Mixpanel.track("unlock_tcard_clicked", {
                widget: "trade_ideas",
                page: "pro_trades_home",
                is_new: ele?.newTradeCard,
                duration_type: ele?.term,
                fincode: ele?.finCode,
                symbol: ele?.stockName
            });
            unLockTradeCard(ele.id).then((res) => getTradeCard(false));
        } else if (ele?.tradeCardLockedStateActionDto?.action === "UNLOCK_TRADE_CARD_WITH_BF") {
            Mixpanel.track("cta_clicked", {
                cta_text: ele?.tradeCardLockedStateActionDto?.action,
                page: "pro_trade_live"
            });
            dispatch(setBuyProPopUp(true));
        } else {
            Mixpanel.track("cta_clicked", {
                cta_text: ele?.tradeCardLockedStateActionDto?.action,
                page: "pro_trade_live"
            });
            if (ele?.tradeCardLockedStateActionDto?.action === "REDIRECT_TO_COMMODITY_PAYMENT") {
                router.push(`/user/payment-commodity?planType=${ele?.tradeCardLockedStateActionDto?.action}`);
            } else {
                router.push(`/user/payment?planType=${ele?.tradeCardLockedStateActionDto?.action}`);
            }
        }
    };

    return (
        <div className=" relative z-[4] w-full p-4 rounded-2xl bg-[#C4E1F4]" id={ele?.id}>
            <div className="flex justify-between mb-3">
                <div className="text-[#606060] text-sm font-medium flex items-center ">
                    <span className=" font-semibold mr-1">{ele?.lastModificationTime ? `Modified:` : `Posted:`}</span>
                    {ele?.lastModificationTime ?? moment(ele.createdAt).format("DD MMM 'YY | hh:mm A")}
                    {(ele?.lastModificationTagImageUrl || (isFuturesOrOptions(ele?.term) && ele?.newTradeCard)) && (
                        <Image
                            src={
                                ele?.lastModificationTagImageUrl ??
                                "https://storage.googleapis.com/app-assets-univest/new_tag.png"
                            }
                            alt="lastModificationTagImageUrl"
                            width={300}
                            height={100}
                            className="h-5 object-left object-contain w-20 ml-3 flex justify-start"
                        />
                    )}
                </div>
                <div className="flex items-center gap-4 absolute -top-[5px] right-6">
                    <div
                        className="flex border rounded-b pl-1 pr-2 py-[3px] justify-between items-center gap-2 bg-white relative"
                        style={{ borderColor: termColor(ele?.term), color: termColor(ele?.term) }}
                    >
                        <svg
                            className="absolute -left-[7px] -right-[7px] -top-[1.5px] z-[-1]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 109 6"
                            fill="none"
                        >
                            <path
                                d="M4.59519 0.953125H104.069L108.332 5.86222H0.332031L4.59519 0.953125Z"
                                fill={termColor(ele?.term)}
                            />
                        </svg>
                        <FaClock className="text-sm" />
                        <div className=" text-xs font-semibold">{termText(ele?.term)}</div>
                    </div>
                </div>
            </div>
            {!ele?.locked ? (
                <div className="flex items-stretch trade-card gap-3">
                    <TradeCardBody
                        ele={ele}
                        calcNetPriceChange={calcNetPriceChange}
                        stockPrices={stockPrices}
                        setStockPrices={setStockPrices}
                        calcPotential={calcPotential}
                        calcNetGainLoss={calcNetGainLoss}
                        onBf={false}
                    />

                    <div className="w-[30%] flex flex-col gap-2 justify-between items-center self-stretch bg-white p-3 rounded-lg">
                        <div className="flex w-full items-start gap-3">
                            <div
                                className={`w-full h-10 flex justify-center items-center flex-[1_0_0] border border-[#DFDFDF] bg-[#FFF] p-2 rounded-lg border-solid ${
                                    isDisabled ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            >
                                <WatchListButton
                                    finCode={ele?.finCode}
                                    exchange={ele?.exchange}
                                    pageName={"live_trades"}
                                    symbol={ele?.stockName}
                                    watchListIds={ele?.watchListIds}
                                    disabled={isDisabled}
                                />
                            </div>
                            <Hint label="Trading view" side={"left"}>
                                <div
                                    className={`cursor-pointer w-full h-10 flex justify-center items-center flex-[1_0_0] border border-[#DFDFDF] bg-[#FFF] p-2 rounded-lg border-solid ${
                                        isDisabled ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                                    onClick={(e) => {
                                        if (!isDisabled) {
                                            Mixpanel.track("cta_clicked", {
                                                cta_text: "chart_view",
                                                page: "pro_trade_live",
                                                tab: ele?.term,
                                                tcard_type: ele?.term,
                                                fincode: ele?.finCode,
                                                symbol: ele?.stockName,
                                                current_gain_loss: calcNetGainLoss(ele)
                                            });
                                            toStockDetailFromTradeCard(router.push, ele, "/charts");
                                        }
                                    }}
                                >
                                    <Image src={tradingView} alt="tradingView" className="min-w-[24px] h-6" />
                                </div>
                            </Hint>
                            {ele?.optionEnabled && (
                                <Hint label="Option chain" side={"left"}>
                                    <div
                                        className={`cursor-pointer w-full h-10 flex justify-center items-center flex-[1_0_0] border border-[#DFDFDF] bg-[#FFF] p-2 rounded-lg border-solid ${
                                            isDisabled ? "opacity-50 cursor-not-allowed" : ""
                                        }`}
                                        onClick={(e) => {
                                            if (!isDisabled) {
                                                Mixpanel.track("cta_clicked", {
                                                    cta_text: "option_chain",
                                                    page: "pro_trade_live",
                                                    tab: ele?.term,
                                                    tcard_type: ele?.term,
                                                    fincode: ele?.finCode,
                                                    symbol: ele?.stockName,
                                                    current_gain_loss: calcNetGainLoss(ele)
                                                });
                                                toStockDetailFromTradeCard(router.push, ele, "/option-chain");
                                            }
                                        }}
                                    >
                                        <Image src={chainIcon} alt="chainIcon" className="w-4 h-4" />
                                    </div>
                                </Hint>
                            )}
                        </div>
                        <div className="flex flex-col justify-center items-center gap-2 self-stretch">
                            <TradeCardBuyBtn
                                tradeCardData={ele}
                                finCode={ele?.finCode}
                                type={ele?.recommendationType}
                                symbol={ele?.compName}
                                source={"live_trades"}
                                tab={term}
                                ele={ele}
                                ltp={stockPrices?.[ele?.finCode]}
                                disabled={isDisabled}
                            />
                            {/* <TradeCardExitBtn
                                    tradeCardData={ele}
                                    finCode={ele?.finCode}
                                    type={ele?.recommendationType}
                                    symbol={ele?.compName}
                                    source={"live_trades"}
                                    tab={term}
                                    ele={ele}
                                    isClosed={false}
                                /> */}
                            <TradeHistoryPopUp
                                ele={ele}
                                key={"details"}
                                tradeId={tradeId}
                                openAboutTrade={openAboutTrade}
                            >
                                <TradeCardBody
                                    ele={ele}
                                    calcNetPriceChange={calcNetPriceChange}
                                    stockPrices={stockPrices}
                                    setStockPrices={setStockPrices}
                                    calcPotential={calcPotential}
                                    calcNetGainLoss={calcNetGainLoss}
                                    onBf={true}
                                />
                            </TradeHistoryPopUp>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {ele?.tradeCardLockedStateActionDto?.headerImage && (
                        <div className="flex  justify-center items-center border border-[#DFDFDF)] bg-[#FFF] px-6  rounded-[40px] border-solid">
                            <Image
                                width={400}
                                height={200}
                                src={ele?.tradeCardLockedStateActionDto?.headerImage}
                                alt="demo"
                            />
                        </div>
                    )}
                    <div
                        className=" mt-4 rounded-2xl"
                        style={{
                            background: `#3c3c3c url(${mini_trade_card_bg?.src}) no-repeat center`
                            // background: `#3c3c3c url(${mini_trade_card_bg?.src}) top center / 200px 200px no-repeat center`
                        }}
                    >
                        <div className="flex flex-col justify-center items-center gap-8  py-16 ">
                            <div className="flex justify-center items-center gap-3 bg-[#148F36] px-6 py-2 rounded-xl">
                                <p className="text-[#FFF] text-center text-xl font-medium leading-8">
                                    Potential left :{" "}
                                    <span className="font-bold">
                                        {isFuturesOrOptions(ele?.term) && "₹"}
                                        {putComma(ele?.potentialLeft, 2)} {isFutures(ele?.term) && " per lot"}
                                        {isOptions(ele?.term) && " per strategy"}
                                        {!isFuturesOrOptions(ele?.term) && "%"}
                                    </span>{" "}
                                </p>
                            </div>
                            {/* <div onClick={() => handleButtonAction(ele)} className='flex justify-center items-center gap-1 bg-[#FFF] pl-3 pr-4 py-1.5 rounded-3xl text-black text-base font-bold leading-7'>
                                    <Image className='w-6' src={locked_icon} alt='' />
                                    {'Unlock free'}
                                </div> */}
                            <motion.button
                                id="open-free-btn"
                                type="button"
                                onTap={() => {
                                    Mixpanel.track("cta_clicked", {
                                        cta_text: "buy",
                                        page: "pro_trade_live",
                                        tab: ele?.term,
                                        tcard_type: ele?.term,
                                        fincode: ele?.finCode,
                                        symbol: ele?.stockName,
                                        in_buying_range: inRangeOrNot(ele, stockPrices?.[ele?.finCode]),
                                        target_hit_miss: null,
                                        tcard_id: ele?.id
                                    });
                                    handleButtonAction(ele);
                                }}
                                className={` flex-[1_0_0] px-3 select-none font-Inter flex justify-center items-center gap-1 bg-[#FFF] pl-3 pr-4 py-1.5 rounded-3xl }`}
                                whileTap={{ scale: 0.95 }}
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                                <>
                                    <Image className="w-6" src={locked_icon} alt="" />
                                    <p className="text-black text-base font-bold leading-7">
                                        {ele?.tradeCardLockedStateActionDto?.btnText}
                                    </p>
                                </>
                            </motion.button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export function TradeCardBody({
    calcNetGainLoss,
    calcNetPriceChange,
    calcPotential,
    ele,
    stockPrices,
    setStockPrices,
    onBf,
    className = ""
}) {
    const { push } = useRouter();
    return (
        <div className={cn("w-[70%] flex flex-col gap-3 items-center ", { "w-full": onBf }, className)}>
            <div
                className={cn(
                    "flex justify-between items-center border w-full py-2 px-4 rounded-lg bg-white border-[#26A649]",
                    {
                        "flex-col": isFuturesOrOptions(ele?.term)
                    }
                )}
            >
                <div className={`w-full flex items-center justify-center `}>
                    <span className="text-[#747474] text-sm font-medium mr-1 whitespace-nowrap">
                        {calcNetGainLoss(ele) >= 0 ? "Gain so far:" : "Loss so far:"}
                    </span>
                    <span
                        className={`px-2 flex text-sm justify-center items-center gap-1 rounded  font-bold leading-6 whitespace-nowrap ${
                            calcNetGainLoss(ele) >= 0
                                ? "bg-[#26A649] text-white"
                                : "border border-[#EB4E2C] text-[#EB4E2C]"
                        }`}
                    >
                        {calcNetGainLoss(ele) >= 0 && "+"}
                        {isFuturesOrOptions(ele?.term) && "₹"}
                        {putComma(calcNetGainLoss(ele))}
                        {!isFuturesOrOptions(ele?.term) && "%"}
                        {(isOptions(ele?.term) || isMcxFuturesOrOptions(ele?.term)) &&
                            ` (${putComma((calcNetGainLoss(ele) * 100) / ele?.marginRequired)}%)`}
                    </span>
                </div>
                <div
                    className={cn("h-3/4 min-h-[20px] w-[1px] min-w-[1px] bg-[#414141] mx-3", {
                        "h-[0.5px] min-h-[0.5px] w-full mx-0 my-2": isFuturesOrOptions(ele?.term)
                    })}
                />
                <div className={`w-full flex items-center justify-center `}>
                    <span className="text-[#747474] text-sm font-medium whitespace-nowrap">Potential left :</span>
                    <span
                        className={cn(
                            "ml-1 text-center text-sm font-bold",
                            calcPotential(ele) >= 0 ? "text-[#26A649]" : "text-[#EB4E2C]"
                        )}
                    >
                        {isFuturesOrOptions(ele?.term) && "₹"}
                        {putComma(calcPotential(ele))}
                        {(isOptions(ele?.term) || isMcxFuturesOrOptions(ele?.term)) &&
                            ` (${putComma((calcPotential(ele) * 100) / ele?.marginRequired)}%)`}
                        {!isFuturesOrOptions(ele?.term) && "%"}
                    </span>
                    <span className="text-[#747474] text-sm font-medium whitespace-nowrap ml-1">
                        {isFutures(ele?.term) && `/ lot`}
                        {isOptions(ele?.term) && `/ strategy`}
                    </span>
                </div>
            </div>
            <div
                className={cn("flex flex-col gap-4 items-center bg-white p-3 rounded-md w-full", { "p-0 pt-3": onBf })}
            >
                <div className="flex justify-between items-center self-stretch ">
                    <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => {
                            toStockDetailFromTradeCard(push, ele);
                        }}
                    >
                        {ele?.logoUrl ? (
                            <Image src={ele?.logoUrl} alt="stock logo" width={44} height={44} />
                        ) : (
                            <div className="h-11 w-11 font-Inter font-extrabold text-[24px] text-[#606060] bg-[#ededed] text-center py-1 rounded-full ">
                                {ele?.title?.slice(0, 1)}
                            </div>
                        )}
                        <div>
                            <p className="text-black text-sm font-bold">{ele?.title}</p>
                            {!isOptions(ele.term) && (
                                <p className="text-black text-xs font-medium whitespace-nowrap line-clamp-1">
                                    ₹{putComma(stockPrices?.[ele?.finCode] || ele?.ltpPrice)}{" "}
                                    <span
                                        style={{
                                            color: calcNetPriceChange(ele).change < 0 ? "#EB4E2C" : "#26A649"
                                        }}
                                        className={` text-right text-xs font-semibold leading-5`}
                                    >
                                        {putComma(calcNetPriceChange(ele).change)} (
                                        {putComma(calcNetPriceChange(ele).percentageChange)}%)
                                    </span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {!isFuturesOrOptions(ele?.term) && ele?.tradeCardModificationsList && (
                    <TradeChangeDetails tradeCardModificationsList={ele?.tradeCardModificationsList} />
                )}
                {isFutures(ele?.term) && (
                    <TradeChangeDetails
                        tradeCardModificationsList={[
                            {
                                action: ele?.recommendationType,
                                price: ele?.suggestedPrice,
                                entryMin: ele?.suggestivePriceMin,
                                entryMax: ele?.suggestivePriceMax
                            }
                        ]}
                    />
                )}

                {isOptions(ele?.term) && ele?.tradeCardOptionsDataList && (
                    <div className="w-full rounded-lg border border-solid border-[#C4E1F4] overflow-hidden">
                        <table className="w-full table-auto text-xs font-medium leading-5 ">
                            <thead>
                                <tr className="text-left bg-[#C4E1F4]">
                                    <th className="px-2 py-1.5">Strategy (execute all)</th>
                                    {ele?.tradeCardOptionsDataList?.filter((option, i) => option?.ratio > 1)?.length >
                                        1 && <th className="px-2 py-1.5">Ratio</th>}
                                    <th className="px-2 py-1.5">Entry </th>
                                    <th className="px-2 py-1.5">LTP </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ele?.tradeCardOptionsDataList?.map((option, i) => (
                                    <TradeOption
                                        key={i}
                                        optionDetails={option}
                                        stockPrices={stockPrices}
                                        setStockPrices={setStockPrices}
                                        showRatio={
                                            ele?.tradeCardOptionsDataList?.filter((option, i) => option?.ratio > 1)
                                                ?.length > 1
                                        }
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="whitespace-nowrap flex items-center justify-between self-stretch ">
                    <div className="flex flex-col justify-center items-start gap-1">
                        <p className="text-[#747474] text-xs font-medium leading-5 flex flex-row gap-2 justify-center items-center">
                            {ele?.modifiedStopLoss ? "Modified stoploss" : "Stoploss"}
                            {isOptions(ele?.term) ? " amount " : " "}
                            {!ele.stopLoss && (
                                <BsInfoCircleFill onClick={() => stopLossPopUp.open()} color="#0862BC" />
                            )}{" "}
                        </p>
                        <p className="text-black text-sm font-bold leading-6">
                            {ele.stopLoss ? `₹${putComma(ele?.stopLoss)}` : "--"}
                        </p>
                    </div>
                    {!isFuturesOrOptions(ele?.term) && ele.netAverageBuy && (
                        <div className="flex flex-col justify-center items-start gap-1">
                            <p className="text-[#747474] text-xs font-medium leading-5">Net average</p>
                            <p className="text-black text-sm font-bold leading-6">
                                {ele.netAverageBuy ? `₹${putComma(ele?.netAverageBuy)}` : "--"}
                            </p>
                        </div>
                    )}
                    <div className="flex flex-col justify-center items-start gap-1">
                        <p className="text-[#747474] text-xs font-medium leading-5">
                            {ele?.modifiedTarget ? "Modified target" : "Target"}
                            {isOptions(ele?.term) ? " amount " : " "}
                        </p>
                        <p className="text-black text-sm font-bold leading-6">
                            {ele.targetPrice ? `₹${putComma(ele?.targetPrice)}` : "--"}
                        </p>
                    </div>
                    {isFuturesOrOptions(ele?.term) ? (
                        <div className="flex flex-col justify-center items-center gap-1">
                            <p className="text-[#747474] text-xs font-medium leading-5">Lot size</p>
                            <p className="text-black text-sm font-bold leading-6">{putComma(ele?.lotSize, 0)}</p>
                        </div>
                    ) : (
                        <div className="flex flex-col justify-center items-end gap-1">
                            <p className="text-[#747474] text-xs font-medium leading-5">Duration</p>
                            <p className="text-black text-sm font-bold leading-6">
                                {ele?.expectedDurationEnum || "--"}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {isFuturesOrOptions(ele?.term) && (
                <div
                    className={cn(
                        "-mt-4 py-1.5 px-3 bg-[#F5F5F5] border-t-[0.5px] border-[#747474] w-full flex justify-center items-center self-stretch rounded-b-lg",
                        {
                            "justify-between": isOptions(ele?.term),
                            "rounded-b-none my-2 border-y-[0.5px] py-1": onBf
                        }
                    )}
                >
                    <div className="flex justify-center items-center gap-1.5 self-stretch px-0 py-1">
                        <p className="text-[#414141] text-justify text-xs font-medium">Margin req. :</p>
                        <p className="text-black text-justify text-xs font-bold">
                            ₹{putComma(ele?.actualMarginRequired)}
                        </p>
                    </div>
                    {isOptions(ele?.term) && (
                        <div className="flex justify-center items-center gap-1.5 self-stretch px-0 py-1">
                            <p className="text-[#414141] text-justify text-xs font-medium">Final margin :</p>
                            <p className="text-black text-justify text-xs font-bold">₹{putComma(ele?.liveMargin)}</p>
                        </div>
                    )}
                </div>
            )}
            {ele?.note && !onBf && (
                <div className={cn("w-full bg-white py-1 rounded text-black text-xs font-semibold px-3")}>
                    {ele?.note}
                </div>
            )}
            {ele?.suggestivePriceMin > 0 &&
                ele?.suggestivePriceMax > 0 &&
                ele?.suggestivePriceMin &&
                ele?.suggestivePriceMax &&
                ((stockPrices?.[ele?.finCode] ?? ele?.ltpPrice) < ele?.suggestivePriceMin ||
                    (stockPrices?.[ele?.finCode] ?? ele?.ltpPrice) > ele?.suggestivePriceMax) && (
                    <div className="items-center w-full bg-[#FFF9F0] py-1 rounded text-[#EF9400] text-center text-[10px] font-bold leading-4">
                        Trade is outside the entry range. Take caution
                    </div>
                )}

            {!isFuturesOrOptions(ele?.term) && ele?.hashtags?.length > 0 && !onBf && (
                <div className="flex text-xs font-bold w-full gap-3 line-clamp-1">
                    {ele?.hashtags?.map((ele) => (
                        <div key={ele} className="text-[#0862BC] underline cursor-pointer">
                            {ele}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function TradeOption({ optionDetails, stockPrices, setStockPrices, showRatio }) {
    useSubscription(`/stock/live-price/${optionDetails?.finCode}`, (res) => {
        const newPrice = JSON.parse(res.body).ltpPrice;
        setStockPrices((prevPrices) => ({
            ...prevPrices,
            [optionDetails?.finCode]: newPrice
        }));
    });
    return (
        <tr className="border-b border-[#EDEDED] text-xs font-semibold">
            <td className="p-2">
                <div className="flex items-center gap-2">
                    <p
                        className={`flex justify-center items-center rounded border ${
                            optionDetails?.recommendationType === "BUY"
                                ? "border-[#148F36] text-[#148F36] "
                                : "border-[#EB4E2C] text-[#EB4E2C]"
                        }  px-[5px] py-0.5 border-solid  text-center text-[8px] font-bold leading-3 `}
                    >
                        {optionDetails?.recommendationType === "BUY" ? "Buy" : "Sell"}
                    </p>
                    {optionDetails?.symbol}
                </div>
            </td>
            {showRatio && <td className="p-2">{putComma(optionDetails?.ratio ?? "0", 0)}</td>}
            <td className="p-2">₹{putComma(optionDetails?.suggestedPrice ?? "0.00")}</td>
            <td className="p-2">
                ₹{putComma(stockPrices?.[optionDetails?.finCode] || (optionDetails?.nseLtpPrice ?? "0.00"))}
            </td>
        </tr>
    );
}

export function TradeChangeDetails({ tradeCardModificationsList, isFuturesCloseOrMCX = false }) {
    return (
        <div className="w-full rounded-lg border border-solid border-[#C4E1F4] overflow-hidden">
            <table className="w-full table-auto text-xs font-medium leading-5 ">
                <thead>
                    <tr className="text-left bg-[#C4E1F4]">
                        {tradeCardModificationsList?.[0]?.date && <th className="px-2 py-1.5">Date</th>}
                        <th className="px-2 py-1.5">Action</th>
                        <th className="px-2 py-1.5">Price</th>
                        {!isFuturesCloseOrMCX && <th className="px-2 py-1.5">Range</th>}
                    </tr>
                </thead>
                <tbody>
                    {tradeCardModificationsList?.map((ele, i) => (
                        <tr key={i} className="border-b border-[#EDEDED]">
                            {ele?.date && <td className="p-2">{ele?.date}</td>}
                            <td className={"p-1 px-2"}>
                                <div
                                    className={cn(
                                        "w-[74px] rounded border text-center text-[10px] font-semibold text-primary border-primary",
                                        {
                                            "border-[#148F36] text-[#148F36] ": ele?.action?.toLowerCase() === "buy",
                                            "border-[#EB4E2C] text-[#EB4E2C]": ele?.action?.toLowerCase() === "sell"
                                        }
                                    )}
                                >
                                    {capitalized(ele?.action)}
                                </div>
                            </td>
                            <td className="p-2">₹{putComma(ele?.price)}</td>
                            {!isFuturesCloseOrMCX && (
                                <td className="p-2">
                                    {ele?.entryMin ? putComma(ele?.entryMin, 1) : ""} -{" "}
                                    {ele?.entryMax ? putComma(ele?.entryMax, 1) : ""}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
